import React, { useState, useEffect, useCallback } from 'react';
import { api } from '@/lib/api';
import { logger } from '@/lib/logger';
import { Trash } from '@phosphor-icons/react';

const ROLE_COLORS = {
  owner: 'bg-purple-50 text-purple-700',
  editor: 'bg-blue-50 text-blue-700',
  viewer: 'bg-gray-50 text-gray-700',
  auditor: 'bg-yellow-50 text-yellow-700',
};

export const CollaboratorsTab = ({ caseId }) => {
  const [collaborators, setCollaborators] = useState([]);
  const [owner, setOwner] = useState(null);
  const [auditLog, setAuditLog] = useState([]);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('viewer');
  const [inviting, setInviting] = useState(false);
  const [showAudit, setShowAudit] = useState(false);

  const loadCollaborators = useCallback(async () => {
    try {
      const { data } = await api.get(`/cases/${caseId}/collaborators`);
      setCollaborators(data.collaborators);
      setOwner(data.owner);
    } catch (err) { logger.error('Load collaborators failed:', err); }
  }, [caseId]);

  useEffect(() => { loadCollaborators(); }, [loadCollaborators]);

  const loadAuditLog = async () => {
    try {
      const { data } = await api.get(`/cases/${caseId}/audit-log`);
      setAuditLog(data.audit_log);
      setShowAudit(true);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to load audit log');
    }
  };

  const inviteCollaborator = async (e) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;
    setInviting(true);
    try {
      await api.post(`/cases/${caseId}/collaborators`, { email: inviteEmail, role: inviteRole });
      setInviteEmail('');
      await loadCollaborators();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to invite');
    } finally {
      setInviting(false);
    }
  };

  const removeCollaborator = async (userId) => {
    if (!window.confirm('Remove this collaborator?')) return;
    try {
      await api.delete(`/cases/${caseId}/collaborators/${userId}`);
      await loadCollaborators();
    } catch {
      alert('Failed to remove');
    }
  };

  return (
    <div data-testid="collaborators-tab" className="space-y-6">
      {/* Invite Form */}
      <div className="bg-white border border-[#E5E7EB] rounded-lg p-6">
        <h3 className="font-outfit text-lg font-semibold text-[#09090B] mb-4">Invite Collaborator</h3>
        <form onSubmit={inviteCollaborator} className="flex gap-3 items-end">
          <div className="flex-1">
            <label className="block text-xs font-bold tracking-[0.15em] uppercase text-[#6B7280] mb-1 font-ibm">Email</label>
            <input data-testid="invite-email-input" type="email" value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} required
              placeholder="colleague@example.com" className="w-full px-3 py-2.5 border border-[#E5E7EB] rounded-md text-sm font-ibm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
          </div>
          <div className="w-36">
            <label className="block text-xs font-bold tracking-[0.15em] uppercase text-[#6B7280] mb-1 font-ibm">Role</label>
            <select data-testid="invite-role-select" value={inviteRole} onChange={e => setInviteRole(e.target.value)}
              className="w-full px-3 py-2.5 border border-[#E5E7EB] rounded-md text-sm font-ibm focus:outline-none focus:ring-2 focus:ring-[#002FA7]">
              <option value="viewer">Viewer</option>
              <option value="editor">Editor</option>
              <option value="auditor">Auditor</option>
            </select>
          </div>
          <button data-testid="invite-submit-button" type="submit" disabled={inviting}
            className="px-4 py-2.5 bg-[#002FA7] text-white font-ibm text-sm font-semibold rounded-md hover:bg-[#00237D] disabled:opacity-50 transition-all">
            {inviting ? 'Inviting...' : 'Invite'}
          </button>
        </form>
      </div>

      {/* Team Members */}
      <div className="bg-white border border-[#E5E7EB] rounded-lg overflow-hidden">
        <div className="p-6 border-b border-[#E5E7EB] flex items-center justify-between">
          <h3 className="font-outfit text-lg font-semibold text-[#09090B]">Team Members</h3>
          <button onClick={loadAuditLog} data-testid="view-audit-log-button" className="text-xs font-ibm text-[#002FA7] hover:underline">View Audit Log</button>
        </div>
        <div className="divide-y divide-[#E5E7EB]">
          {/* Owner */}
          {owner && (
            <div className="flex items-center justify-between px-6 py-4">
              <div>
                <p className="font-ibm font-semibold text-sm text-[#09090B]">{owner.name}</p>
                <p className="text-xs text-[#6B7280] font-ibm">{owner.email}</p>
              </div>
              <span className={`text-xs font-ibm font-semibold px-2 py-0.5 rounded ${ROLE_COLORS.owner}`}>Owner</span>
            </div>
          )}
          {/* Collaborators */}
          {collaborators.map(c => (
            <div key={c._id} className="flex items-center justify-between px-6 py-4" data-testid={`collab-${c.user_id}`}>
              <div>
                <p className="font-ibm font-semibold text-sm text-[#09090B]">{c.name}</p>
                <p className="text-xs text-[#6B7280] font-ibm">{c.email}</p>
              </div>
              <div className="flex items-center gap-2">
                <span className={`text-xs font-ibm font-semibold px-2 py-0.5 rounded ${ROLE_COLORS[c.role] || ROLE_COLORS.viewer}`}>{c.role}</span>
                <button onClick={() => removeCollaborator(c.user_id)} className="p-1 rounded hover:bg-red-50 text-[#6B7280] hover:text-red-600 transition-colors" data-testid={`remove-collab-${c.user_id}`}>
                  <Trash size={14} />
                </button>
              </div>
            </div>
          ))}
          {collaborators.length === 0 && (
            <div className="px-6 py-8 text-center text-sm text-[#6B7280] font-ibm">No collaborators yet. Invite team members above.</div>
          )}
        </div>
      </div>

      {/* Audit Log Modal */}
      {showAudit && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setShowAudit(false)}>
          <div className="bg-white rounded-lg w-full max-w-2xl max-h-[70vh] overflow-y-auto p-6" onClick={e => e.stopPropagation()} data-testid="audit-log-modal">
            <h2 className="font-outfit text-xl font-bold text-[#09090B] mb-4">Audit Log</h2>
            {auditLog.length === 0 ? (
              <p className="text-sm text-[#6B7280] font-ibm">No audit entries yet.</p>
            ) : (
              <div className="space-y-2">
                {auditLog.map((l, i) => (
                  <div key={l._id || `audit-${i}`} className="flex items-start gap-3 p-3 bg-[#F9FAFB] rounded-md border border-[#E5E7EB]">
                    <div className="flex-1">
                      <p className="text-sm font-ibm font-semibold text-[#09090B]">{l.action?.replace(/_/g, ' ')}</p>
                      <p className="text-xs text-[#6B7280] font-ibm">{l.target_type}: {JSON.stringify(l.details || {})}</p>
                    </div>
                    <span className="text-xs text-[#6B7280] font-ibm shrink-0">{new Date(l.created_at).toLocaleString()}</span>
                  </div>
                ))}
              </div>
            )}
            <div className="flex justify-end mt-4">
              <button onClick={() => setShowAudit(false)} className="px-4 py-2 border border-[#E5E7EB] text-[#09090B] font-ibm text-sm rounded-md hover:bg-[#F3F4F6]">Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
